/* @jsx h */
import { computed, defineComponent, h } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import type { TableStatus } from "@lemmonhq/types";
import { useShop } from "@app/vue/providers/ShopConnection";

interface CreditNoteTableSectionProps {
  tableId: number;
  reopen: boolean;
  onToggle: (reopen: boolean) => void;
}

/** "Reopen table" switch for a table sale, with the table's live state. */
export const CreditNoteTableSection = defineComponent<CreditNoteTableSectionProps>(
  {
    name: "CreditNoteTableSection",
    props: ["tableId", "reopen", "onToggle"],
    setup(props) {
      const { t } = useLocalization();
      const { tables } = useShop();

      const tableStatus = computed(() =>
        tables.value.find((table) => table.id === props.tableId),
      );

      const isBusy = computed(() => {
        const current = tableStatus.value as
          | (TableStatus & { is_busy?: boolean })
          | undefined;
        return Boolean(
          current?.table.is_busy ||
          current?.is_busy ||
          (current?.orders?.length ?? 0) > 0,
        );
      });

      return () => (
        <section class="rounded-box border border-base-300 px-3 py-2">
          <label class="flex cursor-pointer items-center gap-3">
            <span class="material-symbols-outlined shrink-0 text-xl text-accent">
              table_restaurant
            </span>
            <div class="min-w-0 flex-1">
              <div class="flex items-center gap-2">
                <span class="truncate text-sm font-medium text-accent">
                  {t("reports.tx.credit_note_items.reopen")} ·{" "}
                  {tableStatus.value?.table.name ||
                    t("reports.tx.reopen_table.unknown_table")}
                </span>
                <span
                  class={`badge badge-xs shrink-0 ${
                    isBusy.value ? "badge-error" : "badge-success"
                  }`}
                >
                  {isBusy.value
                    ? t("reports.tx.reopen_table.busy")
                    : t("reports.tx.reopen_table.free")}
                </span>
              </div>
              <p
                class={`text-xs ${
                  props.reopen && isBusy.value
                    ? "text-error"
                    : "text-base-content/60"
                }`}
              >
                {props.reopen && isBusy.value
                  ? t("reports.tx.credit_note_items.busy_warning")
                  : t("reports.tx.credit_note_items.reopen_body")}
              </p>
            </div>
            <input
              type="checkbox"
              class="toggle toggle-warning toggle-sm shrink-0"
              checked={props.reopen}
              onChange={(e) =>
                props.onToggle((e.target as HTMLInputElement).checked)
              }
            />
          </label>
        </section>
      );
    },
  },
);
